import { FC, Fragment, useReducer } from 'react';

import { ChartPie, MousePointerClick, Percent, Replace, SmilePlus } from 'lucide-react';
import { isEmpty, isNonNullish } from 'remeda';
import useSWR from 'swr';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import css from './CasinoAbTestStatistic.module.css';

import { abTestsService } from 'v2/api/abTestsService';
import { Card } from 'v2/components/card/Card';
import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { Loader } from 'v2/components/loader/Loader';
import { CasinoAbTestModel } from 'v2/types/AbTestsModels';

interface CasinoAbTestStatisticProps {
  test: CasinoAbTestModel;
}

export const CasinoAbTestStatistic: FC<CasinoAbTestStatisticProps> = ({ test }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { data, isLoading, error } = useSWR(
    isOpen && ['getCasinoAbStatistic', test.id],
    abTestsService.getCasinoAbStatistic
  );

  return (
    <Fragment>
      <Tooltip title="Statistic" placement="top">
        <IconButton onClick={setOpen} color="info">
          <ChartPie width={18} height={18} />
        </IconButton>
      </Tooltip>

      <Dialog open={isOpen} onClose={setOpen} maxWidth="sm" fullWidth>
        <DialogTitle>AB Test Statistic</DialogTitle>

        <DialogContent>
          {isLoading ? (
            <Loader />
          ) : (
            <Fragment>
              {error ? (
                <Alert severity="error">The record no longer exists... Check another one</Alert>
              ) : (
                isNonNullish(data) && (
                  <Fragment>
                    {isEmpty(data.variantsStatistics) ? (
                      <EmptyPlaceholder />
                    ) : (
                      <div className={css.casinoAbTestStatistic}>
                        {data.variantsStatistics.map((v, i) => (
                          <div key={v.name} className={css.casinoAbTestStatisticCard}>
                            <p className={css.casinoAbCurrent}>{i === 0 ? 'A' : 'B'}</p>

                            <p className={css.casinoAbCurrentName}>{v.name}</p>

                            <div className={css.casinoAbTestStatisticPoints}>
                              <Card className={css.casinoAbTestStatisticPoint}>
                                <p className={css.casinoAbTestStatisticPointTitle}>
                                  <MousePointerClick width={16} height={16} /> Clicks
                                </p>

                                <p className={css.casinoAbTestStatisticPointValue}>{v.statistics.clicks}</p>
                              </Card>

                              <Card className={css.casinoAbTestStatisticPoint}>
                                <p className={css.casinoAbTestStatisticPointTitle}>
                                  <Replace width={16} height={16} /> Conversions
                                </p>

                                <p className={css.casinoAbTestStatisticPointValue}>{v.statistics.conversions}</p>
                              </Card>

                              <Card className={css.casinoAbTestStatisticPoint}>
                                <p className={css.casinoAbTestStatisticPointTitle}>
                                  <SmilePlus width={16} height={16} /> Impressions
                                </p>

                                <p className={css.casinoAbTestStatisticPointValue}>{v.statistics.impressions}</p>
                              </Card>

                              <Card className={css.casinoAbTestStatisticPoint}>
                                <p className={css.casinoAbTestStatisticPointTitle}>
                                  <Percent width={16} height={16} /> CTR
                                </p>

                                <p className={css.casinoAbTestStatisticPointValue}>{v.statistics.ctr}</p>
                              </Card>

                              <Card className={css.casinoAbTestStatisticPoint}>
                                <p className={css.casinoAbTestStatisticPointTitle}>
                                  <Percent width={16} height={16} /> CR
                                </p>

                                <p className={css.casinoAbTestStatisticPointValue}>{v.statistics.cr}</p>
                              </Card>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Fragment>
                )
              )}
            </Fragment>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={setOpen} color="error" variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};
